import type { ComponentChildren, ComponentProps } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import styles from './ViewportOverlay.module.css';

export interface ViewportOverlayProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  children: ComponentChildren;
  open: boolean;
  onOpenChange: (nextOpen: boolean) => void;
}

/**
 * A shell for details pages that can expand into the browser fullscreen viewport.
 *
 * Wrap the page content in `ViewportOverlay` and control it with the `open` prop.
 * Opening requests fullscreen for the overlay root, while closing exits fullscreen
 * and restores focus to the element that launched the expanded view.
 *
 * Keep fullscreen-specific presentation in the child component styles using the
 * `:fullscreen` pseudo-class. This wrapper should only provide the viewport shell.
 *
 * If fullscreen is unavailable or the request is denied, the overlay closes itself
 * by calling `onOpenChange(false)`.
 */
export function ViewportOverlay({
  children,
  open,
  onOpenChange,
  className,
  ...props
}: ViewportOverlayProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const ownerDocument = rootRef.current?.ownerDocument;

    if (!ownerDocument) {
      return;
    }

    if (open) {
      previouslyFocusedRef.current =
        ownerDocument.activeElement instanceof HTMLElement
          ? ownerDocument.activeElement
          : null;
      return;
    }

    const elementToRestore = previouslyFocusedRef.current;

    if (!elementToRestore) {
      return;
    }

    const restoreId = ownerDocument.defaultView?.requestAnimationFrame(() => {
      elementToRestore.focus();
    });

    previouslyFocusedRef.current = null;

    return () => {
      if (restoreId !== undefined) {
        ownerDocument.defaultView?.cancelAnimationFrame(restoreId);
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const fullscreenElement = rootRef.current;

    if (!fullscreenElement) {
      return;
    }

    const ownerDocument = fullscreenElement.ownerDocument;
    const requestFullscreen =
      fullscreenElement.requestFullscreen?.bind(fullscreenElement);

    if (!requestFullscreen) {
      onOpenChange(false);
      return;
    }

    let cancelled = false;

    const handleFullscreenChange = () => {
      if (!isElementFullscreen(fullscreenElement, ownerDocument)) {
        onOpenChange(false);
      }
    };

    ownerDocument.addEventListener('fullscreenchange', handleFullscreenChange);

    void Promise.resolve(requestFullscreen()).catch(() => {
      if (!cancelled) {
        onOpenChange(false);
      }
    });

    return () => {
      cancelled = true;
      ownerDocument.removeEventListener(
        'fullscreenchange',
        handleFullscreenChange,
      );

      if (isElementFullscreen(fullscreenElement, ownerDocument)) {
        const exitFullscreen =
          ownerDocument.exitFullscreen?.bind(ownerDocument);

        if (exitFullscreen) {
          void exitFullscreen().catch(() => undefined);
        }
      }
    };
  }, [onOpenChange, open]);

  const rootClassName = [styles.root, className].filter(Boolean).join(' ');

  return (
    <div ref={rootRef} className={rootClassName} {...props}>
      {children}
    </div>
  );
}

function isElementFullscreen(
  element: HTMLElement,
  ownerDocument: Document,
): boolean {
  const rootNode = element.getRootNode();

  if (
    rootNode instanceof ShadowRoot &&
    rootNode.fullscreenElement === element
  ) {
    return true;
  }

  return ownerDocument.fullscreenElement === element;
}
